import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useCurrentTeam } from '@/providers/TeamProvider';
import { useInvalidateTeamSalesOrderPhases, useTeamSalesOrderPhases } from '@/hooks/api/0250_team_sales_order_phases';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TeamSalesOrderPhase } from '@/hooks/api/models';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useUpdateTeamSalesOrderPhases } from '@/hooks/api/0270_update_team_sales_order_phase';
import { SimpleTextFormat } from '@/components/element/SimpleTextFormat';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';

type FormInputs = {
  identifier: string;
  team_id: number;
  phase_id: number;
  label: string;
};

export const TeamSalesOrderPhaseEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('チーム別フェーズ名編集'), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `チーム設定`, path: `/w/${_w_!.identifier}/setting/teams` },
        { label: `チーム詳細`, path: `/w/${_w_!.identifier}/setting/teams/${_t_!.id}` },
      ]),
    []
  );

  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_, me, _t_, isAdmin } = useCurrentTeam();
  const { phase_id } = useParams();
  const { data: phases } = useTeamSalesOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  const { mutate, data, isPending, error } = useUpdateTeamSalesOrderPhases();
  const { invalidate } = useInvalidateTeamSalesOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  const [phase, setPhase] = useState<TeamSalesOrderPhase>();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
    },
  });

  useEffect(() => {
    if (phases) {
      const _phase = phases.find((p) => p.id == Number(phase_id));

      if (_phase == null) {
        navigate(`/w/${_w_!.identifier}/setting/teams/${_t_!.id}`);
      } else {
        setValue('phase_id', _phase.id);
        setValue('label', _phase.label);
        setPhase((d) => _phase);
      }
    }
  }, [phases]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  useEffect(() => {
    if (data) {
      toastSuccess('フェーズ名を編集しました');
      invalidate();
      navigate(-1);
    }
  }, [data]);

  if (phase == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'team'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">チーム別フェーズ名編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="col-md-12">
                    <div className="form-group">
                      <label>
                        フェーズ名<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={100}
                        className={`form-control ${errors.label && 'is-invalid'}`}
                        placeholder={'フェーズ名'}
                        {...register('label', {
                          required: '入力して下さい。',
                        })}
                      />
                      {errors.label && <div className="text-xs text-danger mt-1">{errors.label.message}</div>}
                    </div>
                    <div className="form-group">
                      <label>メモ</label>
                      <p className="form-text text-muted text-sm ms-1">
                        <SimpleTextFormat content={phase.description} />
                      </p>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
